import { palm, slash, sprite, star, swirl, waves } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ฝ่ามือมังกรพายุ (พเนจร, hard): the boatman's storm palms. Weathered palms, a slate-blue storm dragon
 * drawn in sea spray and squall lines, white wave crests; plain and blunt, no gold (that is the hidden art's).
 */

const STORM = '#7fa4bc';
const STORM_DK = '#3e6684';
const SPRAY = '#e8f6ff';
const SEA = '#4a8ab0';
const SKIN = '#e2b088';
const SKIN_DK = '#a8744e';

const boatPalm = (p: Pix, x: number, y: number, o: { k?: number; flip?: boolean; flipY?: boolean } = {}) =>
  palm(p, x, y, SKIN, { ...o, lines: SKIN_DK });

/** The storm dragon's head facing right (13×9): horn, mane, open jaw. */
function stormHead(p: Pix, x: number, y: number, flip = false): void {
  sprite(
    p,
    x,
    y,
    [
      '.hh..........',
      'hhhh.hh......',
      '.hbbbbhh.....',
      'hbbbebbbbbb..',
      'hbbbbbbbbbbbw',
      'hbbbbbmmmmmm.',
      '.hbbbbbwwww..',
      '..bbbbbbbbbb.',
      '...hh..h.....',
    ],
    { h: STORM_DK, b: STORM, e: SPRAY, m: STORM_DK, w: SPRAY },
    { flip },
  );
}

/** A wave crest curling to the right. */
function crest(p: Pix, x: number, y: number, w: number): void {
  p.ring(x + w / 2, y + w / 2, w / 2, w / 2, SEA, Math.PI, Math.PI * 1.9);
  p.ring(x + w / 2, y + w / 2 + 1, w / 2 - 1, w / 2 - 1, SPRAY, Math.PI * 1.1, Math.PI * 1.7);
}

/** Squall lines trailing left of x. */
function squall(p: Pix, x: number, rows: readonly (readonly [number, number])[]): void {
  for (const [y, l] of rows) p.rect(x - l, y, l, 1, STORM);
}

registerIcons('skill', {
  // The first palm: straight in like a squall hitting the hull.
  'dp-regret': (p) => {
    squall(p, 13, [
      [9, 9],
      [14, 11],
      [19, 7],
    ]);
    boatPalm(p, 14, 9);
    p.rect(28, 12, 2, 1, SPRAY).rect(28, 17, 2, 1, SPRAY);
  },
  // The tail lash: a spinning back-hand, a long tail arc behind it.
  'dp-tail': (p) => {
    slash(p, 16, 16, 12, Math.PI * 0.6, Math.PI * 1.7, STORM, 4);
    boatPalm(p, 14, 3, { flip: true });
    p.poly([
      [3, 21],
      [8, 24],
      [3, 27],
    ], STORM_DK);
  },
  // A palm pressed to the ground; storm-water rises in crests all round.
  'dp-field': (p) => {
    boatPalm(p, 10, 2, { flipY: true });
    p.rect(3, 25, 26, 2, SEA);
    crest(p, 3, 18, 8);
    crest(p, 21, 18, 8);
    crest(p, 12, 20, 7);
  },
  // Up out of a whirlpool and into the palm strike.
  'dp-leap': (p) => {
    p.ring(10, 25, 8, 3, SEA, 0, Math.PI * 2);
    p.ring(10, 25, 5, 2, SPRAY, 0, Math.PI * 2);
    p.ring(10, 25, 2, 1, STORM_DK, 0, Math.PI * 2);
    p.path([
      [10, 22],
      [11, 16],
      [15, 12],
    ], STORM, 2);
    boatPalm(p, 16, 2);
  },
  // A spinning leap: a waterspout with the palm at its crown.
  'dp-soar': (p) => {
    for (let k = 0; k < 5; k++) {
      const y = 27 - k * 4;
      const r = 3 + k * 2.2;
      p.ring(16, y, r, 1.5, k % 2 ? SEA : STORM, 0, Math.PI * 2);
    }
    p.rect(13, 25, 6, 3, STORM_DK);
    boatPalm(p, 10, 0);
  },
  // Dragon scales: overlapping plates harden over the body.
  'dp-scales': (p) => {
    for (let j = 0; j < 4; j++)
      for (let i = 0; i < 4 - (j % 2); i++) {
        const cx = 8 + i * 6 + (j % 2) * 3;
        const cy = 7 + j * 5;
        p.ell(cx, cy, 3.2, 3, j % 2 ? STORM : STORM_DK);
        p.ring(cx, cy + 0.5, 2.5, 2, SPRAY, 0.3, Math.PI - 0.3);
      }
    p.rect(6, 27, 20, 1, SEA);
  },
  // Still as a dragon in the cloud before the storm.
  'dp-hidden': (p) => {
    stormHead(p, 13, 10);
    for (const [x, y, r] of [
      [8, 20, 4],
      [15, 21, 4.5],
      [23, 21, 4],
      [28, 19, 2.5],
    ] as const) {
      p.ell(x, y, r, r * 0.8, '#b8ccdc');
      p.ell(x - 0.5, y - 0.6, r - 1, (r - 1) * 0.75, SPRAY);
    }
    p.rect(4, 22, 26, 3, '#b8ccdc');
  },
  // Three dragon palms stacked into one, the storm head roaring through.
  'dp-eighteen': (p) => {
    swirl(p, 18, 16, 9, SEA, { arms: 2, turn: 3 });
    boatPalm(p, 3, 4);
    boatPalm(p, 3, 15, { flip: true });
    stormHead(p, 15, 11);
    waves(p, 20, 16, 9, 1, SPRAY, 0.8, 3);
    star(p, 27, 5, 2, SPRAY);
    p.px(26, 26, C.white);
  },
});
